import * as React from 'react';
import * as downloadFile from 'downloadjs';
import { Theme, StyleRules } from '@material-ui/core/styles';
import withStyles, { ClassNameMap } from '@material-ui/core/styles/withStyles';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  LinearProgress,
  Tooltip,
} from '@material-ui/core';
import { GridSize } from '@material-ui/core/Grid';
import { grey } from '@material-ui/core/colors';
import classNames from 'classnames';
import { Comparison as ComparisonType } from '../modules/comparison';
import { UploadFile, UploadFilePattern } from '../modules/upload';
import { ApplyContent } from '../modules/apply';
import SnackbarsMessage from './SnackbarsMessage';
import { Apply } from '../components';
import 'react-tabs/style/react-tabs.css';
import './Comparison.css';

interface Props {
  classes: ClassNameMap;
  files: (UploadFile | null)[];
  filePatterns: UploadFilePattern[];
  goUploadPage: () => void;
  // 比較関連
  compareDocuments: (fileIds: number[], fileTypes: string[]) => void;
  clearComparisonMessages: () => void;
  comparison: ComparisonType | null;
  messages: string[];
  status: number;
  // TSVダウンロード関連
  downloadComparisonDocuments: (fileIds: number[], fileTypes: string[]) => Promise<void>;
  clearComparisonDownloadedMessages: () => void;
  comparisonDownload: Promise<Blob> | null;
  comparisonDownloadedErrorMessages: string[];
  // 申請関連(apply)
  contents: ApplyContent[];
  successMessages: string[];
  errorMessages: string[];
  changeApplyContent: (content: ApplyContent) => void;
  changeCategory: (index: number, content: ApplyContent) => void;
  clearApplyContents: () => void;
  submitApplyContents: (name: string, contents: ApplyContent[]) => Promise<void>;
  clearSuccessMessages: () => void;
  clearErrorMessages: () => void;
}

interface State {
  modalIsOpen: boolean;
}

const TooltipLarge = withStyles({
  tooltip: {
    fontSize: '13px',
    maxWidth: '100%',
  },
})(Tooltip);

const style = (theme: Theme): StyleRules => ({
  root: {
    flexGrow: 1,
  },
  paper: {
    padding: theme.spacing.unit * 2,
  },
  fileName: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  wrapper: {
    paddingLeft: theme.spacing.unit * 2,
    paddingRight: theme.spacing.unit * 2,
  },
  progress: {
    marginTop: theme.spacing.unit * 3,
    padding: theme.spacing.unit * 2,
  },
  divider: {
    backgroundColor: grey[600],
  },
  clickedItem: {
    backgroundColor: '#FFCCFF',
  },
  sameItem: {
    backgroundColor: grey[600],
  },
  check: {
    marginTop: '5px',
  },
  button: {
    margin: theme.spacing.unit,
  },
  highlightContainer: {
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'stretch',
  },
  highlightItem: {
    flex: 1,
    width: '100%',
    padding: 5,
    margin: 5,
  },
});

class Comparison extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      modalIsOpen: false,
    };

    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.backUploadPage = this.backUploadPage.bind(this);
  }

  componentDidMount() {
    const { files, filePatterns, compareDocuments, goUploadPage } = this.props;
    const fileIds: number[] = [];
    const fileTypes: string[] = [];
    files.forEach((file, i) => {
      if (file) {
        fileIds.push(file.id);
        fileTypes.push(filePatterns[i]);
      }
    });
    if (fileIds.length > 0) {
      if (!this.props.comparison) {
        compareDocuments(fileIds, fileTypes);
      }
    } else {
      goUploadPage();
    }
  }

  // 指摘ダイアログ
  openModal() {
    this.setState({ modalIsOpen: true });
  }
  closeModal() {
    this.setState({ modalIsOpen: false });
  }

  backUploadPage() {
    this.props.files.splice(0);
    this.props.files.push(null);
    this.props.files.push(null);
    this.props.files.push(null);
    this.props.goUploadPage();
  }

  // TSVダウンロード
  downloadTSV() {
    const { files, filePatterns, downloadComparisonDocuments } = this.props;
    const fileIds: number[] = [];
    const fileTypes: string[] = [];
    files.forEach((file, i) => {
      if (file) {
        fileIds.push(file.id);
        fileTypes.push(filePatterns[i]);
      }
    });
    if (fileIds.length > 0) {
      downloadComparisonDocuments(fileIds, fileTypes).then(() => {
        const { comparisonDownload } = this.props;
        if (comparisonDownload) {
          comparisonDownload.then((blob) => {
            downloadFile(blob, '比較結果.tsv');
          });
        }
      });
    }
  }

  isClickedItem(
    orgTagId: number,
    position: number,
    order: number,
    text: string,
  ) {
    const { contents } = this.props;
    const exists = contents.filter((value) => {
      return (
        value.orgTagId === orgTagId &&
        value.position === position &&
        value.order === order &&
        value.text === text
      );
    });
    return exists.length;
  }

  isSameItemInClickedItem(text: string) {
    let exists = false;
    const { contents } = this.props;
    for (const c of contents) {
      exists = (c.text === text);
      if (exists) break;
    }
    return exists;
  }

  handleChangeList(
    fileName: string,
    orgTagId: number,
    newTagId: number,
    position: number,
    order: number,
    text: string,
  ) {
    this.props.changeApplyContent({
      fileName,
      position,
      order,
      text,
      orgTagId,
      newTagId,
    });
  }

  render() {
    const {
      classes,
      comparison,
      messages,
      contents,
      status,
      changeCategory,
      clearApplyContents,
      submitApplyContents,
      successMessages,
      errorMessages,
      clearSuccessMessages,
      clearErrorMessages,
      comparisonDownloadedErrorMessages,
      clearComparisonDownloadedMessages,
    } = this.props;

    const xs: GridSize | undefined = comparison ?
      (Math.floor(10 / comparison.documents.length) as GridSize) :
      undefined;

    // TODO: 暫定
    const EXCLUDE_CATEGORY = '9999';

    // tokenチェックエラーの場合、トークンを削除し、画面遷移を発生させ、ログインページに遷移
    if (status === 403 || status === 401) {
      document.cookie = 'access_token=; expires=0';
      document.cookie = 'username=; expires=0';
      this.backUploadPage();
    }

    return (
      <React.Fragment>
        <SnackbarsMessage
          variant="success"
          messages={successMessages}
          onClose={clearSuccessMessages}
        />
        <SnackbarsMessage
          variant="error"
          messages={errorMessages}
          onClose={clearErrorMessages}
        />
        <SnackbarsMessage
          variant="error"
          messages={comparisonDownloadedErrorMessages}  /* FIXME: 共通化したい */
          onClose={clearComparisonDownloadedMessages}
        />
        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          onClick={this.backUploadPage}>
          戻る
        </Button>
        {comparison ? (
          <React.Fragment>
            <Grid
              container
              className={classNames(classes.root, 'comparison-wrap')}
              style={{ overflow: 'scroll', height: '67vh', overflowX: 'hidden', borderTop: 'solid 2px #eee', borderBottom: 'solid 2px #eee', borderLeft: 'solid 2px #eee' }}
              spacing={16}>
              <Grid item xs={12}>
                <div className={classes.wrapper}>
                  <Grid container spacing={16} justify="space-between">
                    <Grid item xs={2} />
                    {comparison.documents.map((doc, i) => (
                      <Grid key={i} item xs={xs}>
                        <TooltipLarge title={doc.fileName}>
                          <Typography variant="h5" className={classes.fileName}>
                            {doc.fileName}
                          </Typography>
                        </TooltipLarge>
                      </Grid>
                    ))}
                  </Grid>
                </div>
              </Grid>
              {Object.keys(comparison.tags).filter((k) => {
                if (k === EXCLUDE_CATEGORY) return false;
                return true;
              }).map((k, i) => (
                <Grid key={i} item xs={12} className="grid-wrapper">
                  <Paper className={classNames(classes.paper, 'grid-outer')}>
                    <Grid
                      container
                      spacing={16}
                      justify="space-between"
                      className="grid-content"
                    >
                      <Grid item xs={2} className="grid-content-title">
                        <Typography
                          variant="h5"
                          className="grid-content-title-h5">{comparison.tags[k]}</Typography>
                      </Grid>
                      {comparison.documents.map((doc, j) => (
                        <Grid key={j} item xs={xs} className="grid-content-item">
                          <Paper>
                            <List disablePadding>
                              {doc.tags[k] && doc.tags[k].map((s, l) => (
                                <React.Fragment key={l}>
                                  {l !== 0 ? <Divider className={classes.divider} /> :
                                    undefined}
                                  {/* j=左:0 右:1 k=カテゴリNo i=カテゴリID l=カテゴリ内の記事ID */}
                                  <ListItem
                                    className={
                                      classNames(
                                        this.isClickedItem(Number(k), j, l, s)
                                          ? classes.clickedItem
                                          : this.isSameItemInClickedItem(s)
                                            ? classes.sameItem : null,
                                      )
                                    }
                                    onClick={() => {
                                      if (
                                        this.isClickedItem(Number(k), j, l, s) ||
                                        !this.isSameItemInClickedItem(s)) {
                                        this.handleChangeList(
                                          doc.fileName, Number(k), Number(k), j, l, s,
                                        );
                                      }
                                    }}>
                                    <ListItemText primary={s} />
                                  </ListItem>
                                </React.Fragment>
                              ))}
                            </List>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            <Grid
              container
              justify="center"
              className={classNames(classes.check, 'print-nodisp')}
            >
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  disabled={contents.filter(x => x).length < 1}
                  onClick={this.openModal}
                >
                  AI間違い指摘
                    </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  onClick={this.downloadTSV.bind(this)}
                >
                  TSVダウンロード
                    </Button>
              </Grid>
            </Grid>
            {/* 申請用ダイアログ */}
            <Dialog
              open={this.state.modalIsOpen}
              onClose={this.closeModal}
              fullWidth={true}
              maxWidth="md"
              aria-labelledby="apply-dialog-title"
            >
              <DialogTitle id="apply-dialog-title">指摘内容を確認してください</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  カテゴリが異なる場合は、正しいカテゴリを設定し、送信ボタンをクリックしてください。
                    </DialogContentText>
                <Apply
                  contents={contents}
                  oldCategories={comparison.tags}
                  onChangeCategory={changeCategory}
                  clearContents={clearApplyContents}
                  onApplyContents={submitApplyContents}
                  onClose={this.closeModal}
                  submitMessages={messages}
                  submitErrorMessages={errorMessages}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={this.closeModal} color="primary">
                  閉じる
                    </Button>
              </DialogActions>
            </Dialog>
          </React.Fragment>
        ) : (messages.length === 0 &&
          <div className={classes.root}>
            <LinearProgress className={classes.progress} />
          </div>
          )
        }
      </React.Fragment>
    );
  }
}

export default withStyles(style)(Comparison);



// WEBPACK FOOTER //
// ./src/components/Comparison.tsx